import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePets } from '../contexts/PetContext';
import api from '../utils/api';
import {
  Camera,
  Send,
  MessageCircle,
  FileText,
  Download,
  AlertTriangle,
  CheckCircle,
  X,
  Plus,
  Heart,
  Brain,
  Loader2,
  ArrowRight,
  Bot,
  User,
  Sparkles,
  Image as ImageIcon
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const MAX_ASSESSMENT_IMAGES = 3;
const RISK_STYLES = {
  low: {
    badge: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    panel: 'border-emerald-100 bg-gradient-to-br from-emerald-50 to-white'
  },
  medium: {
    badge: 'bg-amber-100 text-amber-800 border border-amber-200',
    panel: 'border-amber-100 bg-gradient-to-br from-amber-50 to-white'
  },
  high: {
    badge: 'bg-orange-100 text-orange-800 border border-orange-200',
    panel: 'border-orange-100 bg-gradient-to-br from-orange-50 to-white'
  },
  critical: {
    badge: 'bg-rose-100 text-rose-800 border border-rose-200',
    panel: 'border-rose-100 bg-gradient-to-br from-rose-50 to-white'
  }
};

const Assessment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { pets, loading: petsLoading, fetchPets } = usePets();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPet, setSelectedPet] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imageAnalysis, setImageAnalysis] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [assessmentData, setAssessmentData] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [reportProgress, setReportProgress] = useState(0);
  const [reportProgressLabel, setReportProgressLabel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingAssessment, setExistingAssessment] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [consultMessage, setConsultMessage] = useState('');
  const [consultImageIds, setConsultImageIds] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const assessmentMessagesEndRef = useRef(null);

  const formatSpecies = (species) => {
    if (!species) return '';
    const key = `pets_species_${String(species).toLowerCase()}`;
    const translated = t(key);
    return translated === key ? species : translated;
  };

  const fallbackCopy = {
    en: {
      assess_risk_level: 'Risk level',
      assess_risk_level_hint: 'This indicates how urgent the case appears based on the current chat and uploaded evidence.',
      assess_confidence: 'AI Accuracy',
      assess_confidence_hint: 'This reflects the AI confidence and evidence quality behind this assessment, not the pet health score.',
      assess_report_progress_collecting: 'Collecting chat and image evidence',
      assess_report_progress_waiting: 'Waiting for background worker',
      assess_report_progress_ready: 'Report is ready',
      assess_report_progress_preparing: 'Preparing report',
      assess_included_photos: 'Included Photos',
      assess_likely_issues: 'Likely Issues',
      risk_level_low: 'Low',
      risk_level_medium: 'Medium',
      risk_level_high: 'High',
      risk_level_critical: 'Critical'
    },
    'zh-Hant': {
      assess_risk_level: '\u98a8\u96aa\u7b49\u7d1a',
      assess_risk_level_hint: '\u9019\u4ee3\u8868\u7cfb\u7d71\u6839\u64da\u76ee\u524d\u5c0d\u8a71\u8207\u4e0a\u50b3\u5167\u5bb9\u5224\u65b7\u7684\u500b\u6848\u98a8\u96aa\uff0c\u4e0d\u7b49\u65bc\u6b63\u5f0f\u8a3a\u65b7\u3002',
      assess_confidence: 'AI \u6e96\u78ba\u5ea6',
      assess_confidence_hint: '\u9019\u4ee3\u8868 AI \u5c0d\u672c\u6b21\u8a55\u4f30\u7684\u628a\u63e1\u7a0b\u5ea6\u8207\u8b49\u64da\u5b8c\u6574\u5ea6\uff0c\u4e0d\u4ee3\u8868\u5bf5\u7269\u672c\u8eab\u7684\u5065\u5eb7\u5206\u6578\u3002',
      assess_report_progress_collecting: '\u6b63\u5728\u6574\u7406\u5c0d\u8a71\u8207\u5716\u7247\u8b49\u64da',
      assess_report_progress_waiting: '\u6b63\u5728\u7b49\u5f85\u80cc\u666f\u5de5\u4f5c\u7a0b\u5f0f',
      assess_report_progress_ready: '\u5831\u544a\u5df2\u6e96\u5099\u5b8c\u6210',
      assess_report_progress_preparing: '\u6b63\u5728\u6e96\u5099\u5831\u544a',
      assess_included_photos: '\u5df2\u7d0d\u5165\u7684\u76f8\u7247',
      assess_likely_issues: '\u53ef\u80fd\u554f\u984c',
      risk_level_low: '\u4f4e',
      risk_level_medium: '\u4e2d',
      risk_level_high: '\u9ad8',
      risk_level_critical: '\u7dca\u6025'
    },
    'zh-CN': {
      assess_risk_level: '\u98ce\u9669\u7b49\u7ea7',
      assess_risk_level_hint: '\u8fd9\u4ee3\u8868\u7cfb\u7edf\u6839\u636e\u76ee\u524d\u5bf9\u8bdd\u4e0e\u4e0a\u4f20\u5185\u5bb9\u5224\u65ad\u7684\u4e2a\u6848\u98ce\u9669\uff0c\u4e0d\u7b49\u4e8e\u6b63\u5f0f\u8bca\u65ad\u3002',
      assess_confidence: 'AI \u51c6\u786e\u5ea6',
      assess_confidence_hint: '\u8fd9\u4ee3\u8868 AI \u5bf9\u672c\u6b21\u8bc4\u4f30\u7684\u628a\u63e1\u7a0b\u5ea6\u4e0e\u8bc1\u636e\u5b8c\u6574\u5ea6\uff0c\u4e0d\u4ee3\u8868\u5ba0\u7269\u672c\u8eab\u7684\u5065\u5eb7\u5206\u6570\u3002',
      assess_report_progress_collecting: '\u6b63\u5728\u6574\u7406\u5bf9\u8bdd\u4e0e\u56fe\u7247\u8bc1\u636e',
      assess_report_progress_waiting: '\u6b63\u5728\u7b49\u5f85\u540e\u53f0\u5de5\u4f5c\u8fdb\u7a0b',
      assess_report_progress_ready: '\u62a5\u544a\u5df2\u51c6\u5907\u5b8c\u6210',
      assess_report_progress_preparing: '\u6b63\u5728\u51c6\u5907\u62a5\u544a',
      assess_included_photos: '\u5df2\u7eb3\u5165\u7684\u7167\u7247',
      assess_likely_issues: '\u53ef\u80fd\u95ee\u9898',
      risk_level_low: '\u4f4e',
      risk_level_medium: '\u4e2d',
      risk_level_high: '\u9ad8',
      risk_level_critical: '\u7d27\u6025'
    }
  };

  const textWithFallback = (key) => {
    const translated = t(key);
    const localizedFallback = fallbackCopy[language]?.[key];
    const englishFallback = fallbackCopy.en[key];
    const forceLocalFallbackKeys = new Set([
      'assess_confidence',
      'assess_confidence_hint'
    ]);

    if (forceLocalFallbackKeys.has(key) && (localizedFallback || englishFallback)) {
      return localizedFallback || englishFallback || key;
    }

    if (
      language !== 'en' &&
      localizedFallback &&
      (translated === key || translated === englishFallback)
    ) {
      return localizedFallback;
    }

    if (translated !== key) return translated;
    return localizedFallback || englishFallback || key;
  };

  const formatRiskLevel = (severity) => {
    const normalized = String(severity || 'low').toLowerCase();
    return textWithFallback(`risk_level_${normalized}`);
  };

  const getRiskStyle = (severity) => RISK_STYLES[String(severity || 'low').toLowerCase()] || RISK_STYLES.low;

  const buildApiFileUrl = (value) => {
    if (!value) return '';
    if (/^https?:\/\//i.test(value) || String(value).startsWith('blob:') || String(value).startsWith('data:')) {
      return value;
    }
    const normalized = String(value).startsWith('/') ? String(value) : `/${value}`;
    return `${window.location.origin}${normalized}`;
  };

  const normalizeRecommendationList = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || '').trim()).filter(Boolean);
    }
    if (typeof value === 'string') {
      return value.split('\n').map((item) => item.trim()).filter(Boolean);
    }
    return [];
  };

  const dedupeAssessmentImages = (images) => {
    if (!Array.isArray(images)) return [];

    const seen = new Set();
    return images.filter((image) => {
      const src = buildApiFileUrl(image?.preview || image?.image_file || '');
      const key = [
        image?.original_filename || image?.name || '',
        image?.file_size || image?.size || '',
        image?.uploaded_at || ''
      ].join('|') || src;

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const buildAssessmentViewModel = (payload, petOverride = null, imagesOverride = null) => {
    const recommendations = normalizeRecommendationList(payload?.recommendations);
    const normalizedImages = dedupeAssessmentImages(imagesOverride || payload?.images || uploadedImages);
    const normalizedSections = {
      ...(payload?.sections || {}),
      likely_issues: Array.isArray(payload?.sections?.likely_issues)
        ? payload.sections.likely_issues.map((item) => String(item || '').trim()).filter(Boolean)
        : [],
      visual_findings: Array.isArray(payload?.sections?.visual_findings)
        ? payload.sections.visual_findings.map((item) => String(item || '').trim()).filter(Boolean)
        : [],
      conversation_insights: Array.isArray(payload?.sections?.conversation_insights)
        ? payload.sections.conversation_insights.map((item) => String(item || '').trim()).filter(Boolean)
        : [],
      next_steps: Array.isArray(payload?.sections?.next_steps)
        ? payload.sections.next_steps.map((item) => String(item || '').trim()).filter(Boolean)
        : [],
      urgent_flags: Array.isArray(payload?.sections?.urgent_flags)
        ? payload.sections.urgent_flags.map((item) => String(item || '').trim()).filter(Boolean)
        : []
    };
    return {
      id: payload?.assessment_id || payload?.id,
      pet: petOverride || payload?.pet || selectedPet,
      images: normalizedImages,
      messages: Array.isArray(payload?.messages) ? payload.messages.length : chatMessages.length,
      confidencePercent: Math.round((payload?.confidence_score || 0.85) * 100),
      riskLevel: payload?.severity_level || payload?.severity || 'low',
      recommendations: recommendations.length > 0 ? recommendations : [
        t('assess_report_default_1'),
        t('assess_report_default_2'),
        t('assess_report_default_3')
      ],
      followUp: payload?.follow_up_required ? t('assess_follow_up_recommended') : t('assess_follow_up_routine'),
      report: payload?.report_content || payload?.health_report || t('assess_report_generated'),
      summary: payload?.summary || payload?.report_content || payload?.health_report || t('assess_report_generated'),
      confidence_score: Math.round((payload?.confidence_score || 0.85) * 100),
      sections: normalizedSections,
      pdfDownloadUrl: payload?.pdf_download_url || `/api/assessments/${payload?.assessment_id || payload?.id}/download_report_pdf/`
    };
  };

  const steps = useMemo(() => ([
    { id: 1, name: t('assess_step_select'), description: t('assess_step_select_desc') },
    { id: 2, name: t('assess_step_upload'), description: t('assess_step_upload_desc') },
    { id: 3, name: t('assess_step_chat'), description: t('assess_step_chat_desc') },
    { id: 4, name: t('assess_step_review'), description: t('assess_step_review_desc') }
  ]), [t]);
  const uploadedCount = uploadedImages.length;
  const validUploadedCount = uploadedImages.filter((image) => !image.uploadError).length;
  const hasReachedImageLimit = uploadedCount >= MAX_ASSESSMENT_IMAGES;
  const canContinueFromUploadStep = consultImageIds.length > 0 && !isUploading;

  // The assessment detail reload is intentionally keyed by route/user/language only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user && !pets) {
      fetchPets();
    }
  }, [user, pets, fetchPets]);

  // The localized report refresh should only run when the saved assessment/language/report step changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadExistingAssessment = async () => {
      if (!id || !user) return;

      setIsLoading(true);
      try {
        const response = await api.get(`/assessments/${id}/`, { params: { language } });
        setExistingAssessment(response.data);
        setAssessmentId(response.data.id);

        if (response.data.pet) setSelectedPet(response.data.pet);
        if (response.data.chat_conversation) setChatMessages(response.data.chat_conversation);
        if (response.data.images) setUploadedImages(response.data.images);

        if (response.data.status === 'completed') {
          setCurrentStep(4);
          setAssessmentData(buildAssessmentViewModel(response.data, response.data.pet, response.data.images));
        }
      } catch (loadError) {
        console.error('Failed to load assessment:', loadError);
        setError(t('assess_load_failed'));
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingAssessment();
  }, [id, user, t, language]);

  useEffect(() => {
    const reloadLocalizedAssessment = async () => {
      if (!assessmentId || currentStep !== 4) return;

      try {
        const response = await api.get(`/assessments/${assessmentId}/`, { params: { language } });
        const localizedPet = response.data?.pet || selectedPet;
        const localizedImages = response.data?.images || uploadedImages;

        setExistingAssessment(response.data);
        if (response.data?.pet) setSelectedPet(response.data.pet);
        if (response.data?.images) setUploadedImages(response.data.images);
        setAssessmentData(buildAssessmentViewModel(response.data, localizedPet, localizedImages));
      } catch (reloadError) {
        console.error('Failed to reload localized assessment:', reloadError);
      }
    };

    reloadLocalizedAssessment();
  }, [assessmentId, currentStep, language]);

  useEffect(() => {
    assessmentMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isAnalyzing]);

  const renderFormattedContent = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    const blocks = [];
    let currentList = [];

    const flushList = () => {
      if (currentList.length > 0) {
        blocks.push(
          <ul className="list-disc space-y-1 pl-5" key={`ul-${blocks.length}`}>
            {currentList.map((item, index) => (
              <li key={`li-${blocks.length}-${index}`} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    const boldify = (value) => value.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    lines.forEach((raw) => {
      const line = raw.trim();

      if (line.startsWith('- ')) {
        currentList.push(boldify(line.replace(/^-\s/, '')));
        return;
      }

      if (!line) {
        flushList();
        blocks.push(<div className="h-2" key={`sp-${blocks.length}`} />);
        return;
      }

      flushList();
      blocks.push(
        <p
          className="text-sm leading-6"
          key={`p-${blocks.length}`}
          dangerouslySetInnerHTML={{ __html: boldify(line) }}
        />
      );
    });

    flushList();
    return <div className="space-y-1">{blocks}</div>;
  };

  const ensureAssessment = async () => {
    if (assessmentId) return assessmentId;
    if (!selectedPet) throw new Error(t('assess_select_pet_required'));
    const response = await api.post('/assessments/create_assessment/', { pet_id: selectedPet.id });
    setAssessmentId(response.data.id);
    return response.data.id;
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    setIsDragOver(false);
    if (files.length === 0) return;
    setError('');
    const remainingSlots = Math.max(0, MAX_ASSESSMENT_IMAGES - uploadedImages.length);
    if (remainingSlots === 0) {
      setError(t('assess_image_limit', { count: MAX_ASSESSMENT_IMAGES }));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const acceptedFiles = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      setError(t('assess_image_limit', { count: MAX_ASSESSMENT_IMAGES }));
    }
    setIsUploading(true);

    const newImages = acceptedFiles.map((file, index) => ({
      id: Date.now() + index,
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      isUploading: true,
      uploadError: false
    }));
    setUploadedImages((prev) => [...prev, ...newImages]);

    try {
      const currentAssessmentId = await ensureAssessment();

      for (let i = 0; i < newImages.length; i += 1) {
        const localImage = newImages[i];

        const formData = new FormData();
        formData.append('image', localImage.file);

        try {
          const uploadRes = await api.post(`/assessments/${currentAssessmentId}/upload_image/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          setImageAnalysis((prev) => [
            ...prev,
            {
              id: uploadRes.data.id,
              filename: uploadRes.data.original_filename,
              analysis: uploadRes.data.analysis_results,
              detected_labels: uploadRes.data.detected_labels,
              confidence_scores: uploadRes.data.confidence_scores
            }
          ]);

          setUploadedImages((prev) => prev.map((image) => (
            image.id === localImage.id
              ? { ...image, serverId: uploadRes.data.id, isUploading: false, uploadError: false }
              : image
          )));
        } catch (singleUploadError) {
          console.error('Single image upload failed:', singleUploadError);
          setUploadedImages((prev) => prev.map((image) => (
            image.id === localImage.id
              ? { ...image, isUploading: false, uploadError: true }
              : image
          )));
        }
      }
    } catch (uploadError) {
      console.error('Image upload failed:', uploadError);
      setUploadedImages((prev) => prev.map((image) => (
        newImages.some((pendingImage) => pendingImage.id === image.id)
          ? { ...image, isUploading: false, uploadError: true }
          : image
      )));
      setError(t('assess_upload_failed'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length === 0) return;
    await handleImageUpload({ target: { files } });
  };

  const removeImage = (imageId) => {
    const targetImage = uploadedImages.find((img) => img.id === imageId);
    setConsultImageIds((prev) => prev.filter((id) => id !== imageId));
    setUploadedImages((prev) => {
      const target = prev.find((img) => img.id === imageId);
      if (target?.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(target.preview);
      }
      return prev.filter((img) => img.id !== imageId);
    });
    if (targetImage) {
      setImageAnalysis((prev) => prev.filter((analysisItem) => (
        analysisItem.id !== targetImage.serverId && analysisItem.filename !== targetImage.name
      )));
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedPet) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: newMessage,
      timestamp: new Date().toISOString()
    };

    setChatMessages((prev) => [...prev, userMessage]);
    const messageText = newMessage;
    setNewMessage('');
    setIsAnalyzing(true);

    try {
      const currentAssessmentId = await ensureAssessment();
      const response = await api.post(`/assessments/${currentAssessmentId}/send_message/`, {
        message: messageText
      });

      const bot = response.data.bot_response;
      setChatMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: 'ai',
          content: bot.content,
          intent: bot.intent,
          timestamp: new Date().toISOString()
        }
      ]);
    } catch (chatError) {
      console.error('Chat error:', chatError);
      setChatMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: 'ai',
          content: t('assess_chat_error'),
          timestamp: new Date().toISOString()
        }
      ]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleConsultImage = (imageId) => {
    setConsultImageIds((prev) => (
      prev.includes(imageId) ? prev.filter((id) => id !== imageId) : [...prev, imageId]
    ));
  };

  const consultWithImage = async () => {
    if (consultImageIds.length === 0 || !consultMessage.trim()) return;
    const targetImages = uploadedImages.filter((img) => consultImageIds.includes(img.id) && img.file);
    if (targetImages.length === 0) return;

    setChatMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: 'user',
        content: consultMessage,
        imageCount: targetImages.length,
        imageNames: targetImages.map((image) => image.name),
        isImageConsult: true,
        timestamp: new Date().toISOString()
      }
    ]);
    setIsAnalyzing(true);

    try {
      const currentAssessmentId = await ensureAssessment();
      const responses = [];

      for (let i = 0; i < targetImages.length; i += 1) {
        const targetImage = targetImages[i];
        const formData = new FormData();
        formData.append('image', targetImage.file);
        formData.append('message', consultMessage);

        const response = await api.post(`/assessments/${currentAssessmentId}/consult_with_image/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        responses.push({
          filename: targetImage.name,
          content: response.data?.bot_response?.content || t('assess_image_done')
        });
      }

      const combinedContent = responses.length === 1
        ? responses[0].content
        : responses.map((item, index) => `**${index + 1}. ${item.filename}**\n${item.content}`).join('\n\n');

      setChatMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: 'ai',
          content: combinedContent,
          intent: 'ImageConsultation',
          timestamp: new Date().toISOString()
        }
      ]);
      setConsultMessage('');
      setConsultImageIds([]);
    } catch (consultError) {
      console.error('Image consultation error:', consultError);
      setChatMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: 'ai',
          content: t('assess_image_failed'),
          timestamp: new Date().toISOString()
        }
      ]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const generateReport = async () => {
    if (!selectedPet) return;
    setIsGeneratingReport(true);
    setError('');
    setReportProgress(8);
    setReportProgressLabel(textWithFallback('assess_report_progress_collecting'));

    try {
      const currentAssessmentId = await ensureAssessment();
      const queueResponse = await api.post(`/assessments/${currentAssessmentId}/generate_report/`, {
        async: true,
        language
      });
      const taskId = queueResponse.data?.task_id;
      if (!taskId) throw new Error('missing-report-task-id');
      let completedPayload = null;
      for (let attempt = 0; attempt < 90; attempt += 1) {
        const statusResponse = await api.get(`/assessments/task_status/?task_id=${encodeURIComponent(taskId)}`);
        const state = statusResponse.data?.state;
        const meta = statusResponse.data?.meta || {};

        if (typeof meta.progress === 'number') {
          setReportProgress(Math.max(8, Math.min(100, meta.progress)));
        }
        if (meta.label) {
          setReportProgressLabel(meta.label);
        }

        if (state === 'SUCCESS') {
          completedPayload = statusResponse.data?.result;
          break;
        }
        if (state === 'FAILURE') {
          throw new Error(statusResponse.data?.error || 'report-task-failed');
        }

        if (state === 'PENDING' && attempt > 0) {
          setReportProgress((prev) => Math.min(prev + 3, 92));
          if (!meta.label) setReportProgressLabel(textWithFallback('assess_report_progress_waiting'));
        }

        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      if (!completedPayload) {
        throw new Error('report-task-timeout');
      }

      setAssessmentData(buildAssessmentViewModel(
        { ...completedPayload, assessment_id: currentAssessmentId },
        selectedPet,
        uploadedImages
      ));
      setReportProgress(100);
      setReportProgressLabel(textWithFallback('assess_report_progress_ready'));
      setCurrentStep(4);
    } catch (reportError) {
      console.error('Report generation error:', reportError);
      setError(t('assess_report_error'));
    } finally {
      window.setTimeout(() => {
        setReportProgress(0);
        setReportProgressLabel('');
      }, 500);
      setIsGeneratingReport(false);
    }
  };

  const downloadReport = async () => {
    if (!assessmentData?.pdfDownloadUrl) return;
    setIsDownloadingReport(true);
    try {
      const separator = assessmentData.pdfDownloadUrl.includes('?') ? '&' : '?';
      const response = await api.get(
        `${assessmentData.pdfDownloadUrl.replace(/^\/api/, '')}${separator}language=${encodeURIComponent(language)}`,
        { responseType: 'blob' }
      );
      const fileUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `${t('assess_report_file')}-${selectedPet?.name || 'pet'}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(fileUrl);
    } catch (downloadError) {
      console.error('Download report failed:', downloadError);
      setError(t('assess_report_error'));
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const printReport = () => {
    if (!assessmentData) return;
    const printableImages = (assessmentData.images || [])
      .map((image) => {
        const src = buildApiFileUrl(image.preview || image.image_file);
        if (!src) return '';
        const alt = image.name || image.original_filename || 'assessment-image';
        return `<figure class="photo-card"><img src="${src}" alt="${alt}" /><figcaption>${alt}</figcaption></figure>`;
      })
      .join('');

    const reportHtml = `<!DOCTYPE html>
    <html>
      <head>
        <meta charset='utf-8' />
        <title>${t('assess_print_title')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #111827; }
          h1 { font-size: 20px; margin-bottom: 8px; }
          h2 { font-size: 16px; margin-top: 16px; }
          .section { margin-top: 16px; }
          .score { font-size: 24px; color: #2563eb; }
          ul { margin: 8px 0; padding-left: 18px; }
          .photos { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-top: 12px; }
          .photo-card { border: 1px solid #dbeafe; border-radius: 14px; overflow: hidden; background: #f8fbff; }
          .photo-card img { width: 100%; height: 220px; object-fit: cover; display: block; }
          .photo-card figcaption { padding: 8px 10px; font-size: 12px; color: #475569; }
        </style>
      </head>
      <body>
        <h1>${t('assess_print_title')}</h1>
        <div>${t('assess_pet_info')}: ${assessmentData.pet.name} - ${formatSpecies(assessmentData.pet.species)}${assessmentData.pet.breed ? ` - ${assessmentData.pet.breed}` : ''}</div>
        <div class='section'>
          <h2>${textWithFallback('assess_risk_level')}</h2>
          <div>${formatRiskLevel(assessmentData.riskLevel)}</div>
        </div>
        <div class='section'>
          <h2>${textWithFallback('assess_confidence')}</h2>
          <div class='score'>${assessmentData.confidencePercent}%</div>
        </div>
        <div class='section'>
          <h2>${t('assess_summary')}</h2>
          <div>${assessmentData.report}</div>
        </div>
        ${printableImages ? `<div class='section'><h2>${textWithFallback('assess_included_photos')}</h2><div class='photos'>${printableImages}</div></div>` : ''}
        <div class='section'>
          <h2>${t('assess_recommendations')}</h2>
          <ul>${(assessmentData.recommendations || []).map((rec) => `<li>${rec}</li>`).join('')}</ul>
        </div>
        <div class='section'>
          <h2>${t('assess_follow_up')}</h2>
          <div>${assessmentData.followUp}</div>
        </div>
      </body>
    </html>`;

    const popup = window.open('', '_blank');
    if (!popup) return;
    popup.document.open();
    popup.document.write(reportHtml);
    popup.document.close();
    const triggerPrint = () => {
      popup.focus();
      popup.print();
      popup.close();
    };
    const images = popup.document.images;
    if (!images.length) {
      window.setTimeout(triggerPrint, 250);
      return;
    }
    let loaded = 0;
    const done = () => {
      loaded += 1;
      if (loaded >= images.length) {
        window.setTimeout(triggerPrint, 200);
      }
    };
    Array.from(images).forEach((img) => {
      if (img.complete) {
        done();
      } else {
        img.onload = done;
        img.onerror = done;
      }
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-blue-600" />
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {isGeneratingReport && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/35 px-4 backdrop-blur-[2px]">
          <div className="w-full max-w-lg rounded-3xl border border-white/70 bg-white/95 p-6 shadow-2xl">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-100">
                <Loader2 className="h-7 w-7 animate-spin text-sky-600" />
              </div>
              <div>
                <p className="text-lg font-semibold text-slate-900">{t('assess_generating_report')}</p>
                <p className="text-sm text-slate-500">{reportProgressLabel || textWithFallback('assess_report_progress_preparing')}</p>
              </div>
            </div>
            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between text-sm font-medium text-slate-700">
                <span>{reportProgressLabel || textWithFallback('assess_report_progress_preparing')}</span>
                <span>{reportProgress}%</span>
              </div>
              <div className="h-3 w-full rounded-full bg-sky-100">
                <div
                  className="h-3 rounded-full bg-sky-500 transition-all duration-500"
                  style={{ width: `${reportProgress}%` }}
                />
              </div>
              <p className="mt-3 text-xs text-slate-500">{t('assess_generating_report_hint')}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {existingAssessment ? t('assess_edit') : t('assess_title')}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {existingAssessment ? t('assess_subtitle_edit') : t('assess_subtitle_new')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl py-6 sm:px-6 lg:px-8">
        <div className="mb-8">
          <nav aria-label="Progress">
            <ol className="flex items-center justify-center">
              {steps.map((step, index) => (
                <li key={step.id} className={`relative ${index !== steps.length - 1 ? 'pr-8 sm:pr-20' : ''}`}>
                  <div className="flex items-center">
                    <div className={`relative flex h-10 w-10 items-center justify-center rounded-full ${
                      currentStep >= step.id ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {currentStep > step.id ? (
                        <CheckCircle className="h-6 w-6" />
                      ) : (
                        <span className="text-sm font-medium">{step.id}</span>
                      )}
                    </div>
                    <div className="ml-4 min-w-0">
                      <p className={`text-sm font-medium ${currentStep >= step.id ? 'text-blue-600' : 'text-gray-500'}`}>{step.name}</p>
                      <p className="text-sm text-gray-500">{step.description}</p>
                    </div>
                  </div>
                  {index !== steps.length - 1 && <div className="absolute left-5 top-5 h-0.5 w-full bg-gray-200" />}
                </li>
              ))}
            </ol>
          </nav>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {currentStep === 1 && (
              <div className="rounded-lg bg-white shadow">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">{t('assess_select_pet')}</h3>
                  {petsLoading ? (
                    <div className="py-8 text-center">
                      <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-blue-600" />
                      <p className="text-gray-600">{t('loading_pets')}</p>
                    </div>
                  ) : pets && pets.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {pets.map((pet) => (
                        <div
                          key={pet.id}
                          onClick={() => setSelectedPet(pet)}
                          className={`cursor-pointer rounded-lg border-2 p-4 transition-all ${
                            selectedPet?.id === pet.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                              <Heart className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                              <h4 className="text-sm font-medium text-gray-900">{pet.name}</h4>
                              <p className="text-sm text-gray-500">{formatSpecies(pet.species)}{pet.breed ? ` · ${pet.breed}` : ''}</p>
                              <p className="text-xs text-gray-400">{t('pets_age')}: {pet.age}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <Heart className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                      <h3 className="mb-2 text-lg font-medium text-gray-900">{t('assess_no_pets_found')}</h3>
                      <p className="mb-4 text-gray-600">{t('assess_need_pet_first')}</p>
                      <button onClick={() => navigate('/pets')} className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700">
                        <Plus className="mr-2 h-4 w-4" />
                        {t('assess_add_first_pet')}
                      </button>
                    </div>
                  )}
                  <div className="mt-6">
                    <button
                      onClick={() => setCurrentStep(2)}
                      disabled={!selectedPet}
                      className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t('assess_continue_to_upload')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="rounded-lg bg-white shadow">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">{t('assess_upload_photos')}</h3>
                  {error && (
                    <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
                      <div className="flex">
                        <AlertTriangle className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                          <p className="text-sm text-red-800">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div
                    onClick={() => {
                      if (!hasReachedImageLimit) fileInputRef.current?.click();
                    }}
                    onDragEnter={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (hasReachedImageLimit) return;
                      setIsDragOver(true);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (hasReachedImageLimit) return;
                      setIsDragOver(true);
                    }}
                    onDragLeave={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setIsDragOver(false);
                    }}
                    onDrop={handleDrop}
                    className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                      hasReachedImageLimit
                        ? 'cursor-not-allowed border-slate-200 bg-slate-100 opacity-75'
                        : isDragOver
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Camera className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-900">{t('assess_upload_area_title')}</p>
                      <p className="text-sm text-gray-500">{t('assess_upload_area_hint')}</p>
                      <p className="mt-2 text-xs font-medium text-blue-700">
                        {t('assess_upload_count', { count: uploadedCount, max: MAX_ASSESSMENT_IMAGES })}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {t('assess_upload_multi_hint', { count: MAX_ASSESSMENT_IMAGES })}
                      </p>
                      {hasReachedImageLimit && (
                        <p className="mt-2 text-xs font-semibold text-amber-700">
                          {t('assess_upload_limit_reached')}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!hasReachedImageLimit) fileInputRef.current?.click();
                        }}
                        disabled={hasReachedImageLimit}
                        className={`mt-4 inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors ${
                          hasReachedImageLimit
                            ? 'cursor-not-allowed bg-slate-400'
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {t('chat_upload')}
                      </button>
                    </div>
                    <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </div>

                  {uploadedImages.length > 0 && (
                    <div className="mt-6">
                      <h4 className="mb-3 text-sm font-medium text-gray-900">{t('assess_uploaded_images')}</h4>
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                        {uploadedImages.map((image) => (
                          <div key={image.id} className="relative">
                            <img src={image.preview} alt={image.name} className="h-24 w-full rounded-lg object-cover" />
                            {image.isUploading && (
                              <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-slate-900/45">
                                <div className="flex items-center rounded-full bg-white/95 px-3 py-1 text-xs font-medium text-slate-700 shadow">
                                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                                  {t('chat_uploading')}
                                </div>
                              </div>
                            )}
                            {image.uploadError && (
                              <div className="absolute inset-x-1 bottom-8 rounded bg-red-600/90 px-2 py-1 text-center text-[10px] font-medium text-white">
                                {t('assess_upload_failed')}
                              </div>
                            )}
                            <button onClick={() => removeImage(image.id)} className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1 text-white transition-colors hover:bg-red-600">
                              <X className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => toggleConsultImage(image.id)}
                              disabled={image.isUploading || image.uploadError}
                              className={`absolute bottom-1 left-1 rounded px-2 py-1 text-[10px] ${
                                consultImageIds.includes(image.id) ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'
                              } ${image.isUploading || image.uploadError ? 'cursor-not-allowed opacity-60' : ''}`}
                            >
                              {t('assess_consult')}
                            </button>
                          </div>
                        ))}
                      </div>
                      {isUploading && (
                        <div className="mt-3 flex items-center text-sm text-gray-500">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('assess_analyzing_images')}
                        </div>
                      )}
                      {imageAnalysis.length > 0 && (
                        <div className="mt-4 rounded-lg bg-blue-50 p-3">
                          <h5 className="mb-2 text-sm font-medium text-blue-900">{t('assess_image_analysis')}</h5>
                          <ul className="space-y-1 text-xs text-blue-800">
                            {imageAnalysis.map((analysisItem) => (
                              <li key={analysisItem.id}>
                                <span className="font-semibold">{analysisItem.filename}:</span>{' '}
                                {analysisItem.analysis?.health_indicators?.potential_issues?.length ? t('assess_possible_issues') : t('assess_no_obvious_concerns')}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-6 flex justify-between">
                    <button onClick={() => setCurrentStep(1)} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                      {t('assess_back')}
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          if (validUploadedCount === 0) {
                            setError(t('assess_upload_required_before_continue'));
                            return;
                          }
                          if (consultImageIds.length === 0) {
                            setError(t('assess_select_consult_image_before_continue'));
                            return;
                          }
                          await ensureAssessment();
                          setCurrentStep(3);
                        } catch (ensureError) {
                          console.error('Ensure assessment failed:', ensureError);
                          setError(t('assess_select_pet_before_continue'));
                        }
                      }}
                      disabled={!canContinueFromUploadStep}
                      className={`rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors ${
                        canContinueFromUploadStep
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'cursor-not-allowed bg-slate-400'
                      }`}
                    >
                      {t('assess_continue')}
                      <ArrowRight className="ml-2 inline h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="overflow-hidden rounded-[28px] border border-slate-200/70 bg-white/92 shadow-[0_22px_60px_-28px_rgba(15,23,42,0.32)] backdrop-blur">
                <div className="px-4 py-5 sm:p-6">
                  {error && (
                    <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
                      <div className="flex">
                        <AlertTriangle className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                          <p className="text-sm text-red-800">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mb-5 flex flex-col gap-4 rounded-[24px] border border-slate-200/70 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.14),_transparent_42%),linear-gradient(135deg,rgba(248,250,252,0.98),rgba(239,246,255,0.9))] p-5 shadow-sm">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="min-w-0">
                        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-blue-200/80 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-blue-700">
                          <Sparkles className="h-3.5 w-3.5" />
                          {t('assess_chat_ai')}
                        </div>
                        <h3 className="text-xl font-semibold leading-7 text-slate-900">{t('assess_chat_start')}</h3>
                        <p className="mt-1 text-sm text-slate-600">{t('assess_chat_start_hint')}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-medium text-white">
                          {selectedPet?.name || t('assess_not_selected')}
                        </span>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm ring-1 ring-slate-200">
                          {t('assess_photos_count', { count: uploadedImages.length })}
                        </span>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm ring-1 ring-slate-200">
                          {t('assess_messages_count', { count: chatMessages.length })}
                        </span>
                      </div>
                    </div>
                  </div>

                  {uploadedImages.length > 0 && (
                    <div className="mb-6 rounded-[24px] border border-blue-200/70 bg-[linear-gradient(135deg,rgba(239,246,255,0.95),rgba(255,255,255,0.96))] p-4 shadow-sm">
                      <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{t('assess_image_consultation')}</p>
                          <p className="mt-1 text-xs leading-5 text-slate-600">
                            {consultImageIds.length > 0 ? t('assess_selected_images', { count: consultImageIds.length }) : t('assess_select_images_first')}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {uploadedImages
                            .filter((image) => consultImageIds.includes(image.id))
                            .map((image) => (
                              <span key={image.id} className="inline-flex max-w-[180px] items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm ring-1 ring-slate-200">
                                <ImageIcon className="h-3.5 w-3.5 text-blue-600" />
                                <span className="truncate">{image.name}</span>
                              </span>
                            ))}
                        </div>
                      </div>
                      <div className="flex flex-col gap-3 md:flex-row">
                        <input
                          type="text"
                          value={consultMessage}
                          onChange={(event) => setConsultMessage(event.target.value)}
                          placeholder={consultImageIds.length > 0 ? t('assess_ask_image_placeholder') : t('assess_select_image_first')}
                          className="flex-1 rounded-2xl border border-blue-200 bg-white/95 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                        />
                        <button
                          onClick={consultWithImage}
                          disabled={consultImageIds.length === 0 || !consultMessage.trim() || isAnalyzing}
                          className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                        >
                          <ImageIcon className="mr-2 h-4 w-4" />
                          {t('assess_ask_with_image')}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="mb-4 h-[34rem] overflow-y-auto rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(248,250,252,0.95),rgba(255,255,255,0.98))] p-4 shadow-inner">
                    {chatMessages.length === 0 ? (
                      <div className="flex h-full flex-col items-center justify-center text-center text-slate-500">
                        <div className="mb-4 rounded-full bg-blue-100 p-4">
                          <Brain className="h-10 w-10 text-blue-600" />
                        </div>
                        <p className="text-base font-medium text-slate-900">{t('assess_chat_start')}</p>
                        <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">{t('assess_chat_start_hint')}</p>
                      </div>
                    ) : (
                      <div className="space-y-5">
                        {chatMessages.map((message) => (
                          <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`flex max-w-[88%] items-start gap-3 lg:max-w-[72%] ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
                              <div className={`mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl shadow-sm ${
                                message.type === 'user'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {message.type === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                              </div>
                              <div
                                className={`rounded-[24px] px-4 py-3 shadow-sm ring-1 ${
                                  message.type === 'user'
                                    ? 'bg-blue-50 text-slate-900 ring-blue-200'
                                    : 'bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.95))] text-slate-900 ring-slate-200 shadow-[0_16px_36px_-24px_rgba(15,23,42,0.45)]'
                                }`}
                              >
                                {message.type === 'ai' && (
                                  <div className="mb-3 flex items-center gap-2 border-b border-slate-200 pb-3">
                                    <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700 ring-1 ring-blue-100">
                                      <Sparkles className="h-3.5 w-3.5" />
                                      AI Vet
                                    </span>
                                  </div>
                                )}
                                {message.isImageConsult && (
                                  <div className={`mb-3 flex flex-wrap gap-2 border-b pb-3 ${
                                    message.type === 'user' ? 'border-blue-200' : 'border-slate-200'
                                  }`}>
                                    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
                                      message.type === 'user'
                                        ? 'bg-white text-blue-700 ring-1 ring-blue-200'
                                        : 'bg-blue-50 text-blue-700'
                                    }`}>
                                      <ImageIcon className="h-3.5 w-3.5" />
                                      {message.imageCount} {message.imageCount > 1 ? 'Images' : 'Image'}
                                    </span>
                                    {(message.imageNames || []).map((name) => (
                                      <span
                                        key={name}
                                        className={`inline-flex max-w-[180px] items-center rounded-full px-3 py-1 text-xs ${
                                          message.type === 'user'
                                            ? 'bg-white text-slate-700 ring-1 ring-blue-100'
                                            : 'bg-slate-100 text-slate-700'
                                        }`}
                                      >
                                        <span className="truncate">{name}</span>
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <div className={`${message.type === 'user' ? 'text-slate-800' : 'text-slate-700'}`}>
                                  {renderFormattedContent(message.content)}
                                </div>
                                <div className={`mt-3 flex items-center gap-2 text-[11px] ${
                                  message.type === 'user' ? 'text-slate-500' : 'text-slate-400'
                                }`}>
                                  <span>
                                    {new Date(message.timestamp).toLocaleTimeString('en-HK', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: false,
                                      timeZone: 'Asia/Hong_Kong'
                                    })}
                                  </span>
                                  {message.intent && (
                                    <span className={`rounded-full px-2.5 py-1 font-medium ${
                                      message.type === 'user'
                                        ? 'bg-white text-blue-700 ring-1 ring-blue-100'
                                        : 'bg-slate-100 text-slate-600'
                                    }`}>
                                      {message.intent}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {isAnalyzing && (
                          <div className="flex justify-start">
                            <div className="flex max-w-[88%] items-start gap-3 lg:max-w-[72%]">
                              <div className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-100 text-blue-700 shadow-sm">
                                <Bot className="h-4 w-4" />
                              </div>
                              <div className="rounded-[24px] bg-white px-4 py-3 text-slate-900 shadow-sm ring-1 ring-slate-200">
                                <div className="flex items-center">
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-600" />
                                  <p className="text-sm text-slate-700">{t('chat_thinking')}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        <div ref={assessmentMessagesEndRef} />
                      </div>
                    )}
                  </div>

                  <div className="rounded-[24px] border border-slate-200/80 bg-white/95 p-3 shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(event) => setNewMessage(event.target.value)}
                      onKeyPress={(event) => event.key === 'Enter' && sendMessage()}
                      placeholder={t('assess_message_placeholder')}
                        className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />
                      <button
                        onClick={sendMessage}
                        disabled={!newMessage.trim()}
                        className="inline-flex items-center justify-center rounded-2xl bg-blue-600 px-5 py-3 text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                      >
                        <Send className="mr-2 h-4 w-4" />
                        {t('chat_send')}
                    </button>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-between">
                    <button onClick={() => setCurrentStep(2)} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                      {t('assess_back')}
                    </button>
                    <button
                      onClick={() => {
                        generateReport();
                      }}
                      disabled={isGeneratingReport}
                      className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      {isGeneratingReport ? t('assess_generating_report') : t('assess_generate')}
                      {isGeneratingReport ? <Loader2 className="ml-2 inline h-4 w-4 animate-spin" /> : <ArrowRight className="ml-2 inline h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="rounded-lg bg-white shadow">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">{t('assess_report')}</h3>

                  {error && (
                    <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
                      <div className="flex">
                        <AlertTriangle className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                          <p className="text-sm text-red-800">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {isGeneratingReport ? (
                    <div className="py-8 text-center">
                      <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-blue-600" />
                      <p className="text-lg font-medium text-gray-900">{t('assess_generating_report')}</p>
                      <p className="text-sm text-gray-500">{t('assess_generating_report_hint')}</p>
                    </div>
                  ) : assessmentData ? (
                    <div className="space-y-6">
                      <div className="grid gap-4 lg:grid-cols-2">
                        <div className={`rounded-3xl border p-5 shadow-sm ${getRiskStyle(assessmentData.riskLevel).panel}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">{textWithFallback('assess_risk_level')}</p>
                              <p className="mt-3 text-3xl font-semibold text-slate-900">{formatRiskLevel(assessmentData.riskLevel)}</p>
                              <p className="mt-2 text-sm text-slate-600">{textWithFallback('assess_risk_level_hint')}</p>
                            </div>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getRiskStyle(assessmentData.riskLevel).badge}`}>
                              {formatRiskLevel(assessmentData.riskLevel)}
                            </span>
                          </div>
                        </div>

                        <div className="rounded-3xl border border-sky-100 bg-gradient-to-br from-sky-50 to-white p-5 shadow-sm">
                          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">{textWithFallback('assess_confidence')}</p>
                          <p className="mt-3 text-3xl font-semibold text-slate-900">{assessmentData.confidencePercent}%</p>
                          <p className="mt-2 text-sm text-slate-600">{textWithFallback('assess_confidence_hint')}</p>
                          <div className="mt-4 h-2.5 w-full rounded-full bg-sky-100">
                            <div className="h-2.5 rounded-full bg-sky-500 transition-all duration-700" style={{ width: `${assessmentData.confidencePercent}%` }} />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg bg-gray-50 p-4">
                        <h4 className="mb-2 font-medium text-gray-900">{t('assess_pet_info')}</h4>
                        <p className="text-sm text-gray-600">
                          {assessmentData.pet.name} - {formatSpecies(assessmentData.pet.species)}{assessmentData.pet.breed ? ` - ${assessmentData.pet.breed}` : ''}
                        </p>
                      </div>


                      <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <h4 className="mb-2 font-medium text-gray-900">{t('assess_summary')}</h4>
                        <p className="text-sm text-gray-600">{assessmentData.summary || assessmentData.report}</p>
                      </div>

                      {assessmentData.images?.length > 0 && (
                        <div className="rounded-lg border border-sky-100 bg-sky-50/70 p-4">
                          <h4 className="mb-3 font-medium text-gray-900">{textWithFallback('assess_included_photos')}</h4>
                          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                            {assessmentData.images.map((image) => (
                              <img
                                key={image.id}
                                src={image.preview || image.image_file}
                                alt={image.name || image.original_filename}
                                className="h-24 w-full rounded-2xl border border-white/70 object-cover shadow-sm"
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {assessmentData.sections?.likely_issues?.length > 0 && (
                        <div className="rounded-lg border border-amber-100 bg-amber-50 p-4">
                          <h4 className="mb-2 font-medium text-gray-900">{textWithFallback('assess_likely_issues')}</h4>
                          <ul className="space-y-1">
                            {assessmentData.sections.likely_issues.map((item, index) => (
                              <li key={`${item}-${index}`} className="text-sm text-gray-700">
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="rounded-lg bg-green-50 p-4">
                        <h4 className="mb-2 font-medium text-gray-900">{t('assess_recommendations')}</h4>
                        <ul className="space-y-1">
                          {assessmentData.recommendations.map((recommendation, index) => (
                            <li key={`${recommendation}-${index}`} className="flex items-start text-sm text-gray-600">
                              <CheckCircle className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                              {recommendation}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="rounded-lg bg-yellow-50 p-4">
                        <h4 className="mb-2 font-medium text-gray-900">{t('assess_follow_up')}</h4>
                        <p className="text-sm text-gray-600">{assessmentData.followUp}</p>
                      </div>

                      <div className="flex space-x-4">
                        <button onClick={downloadReport} disabled={isDownloadingReport} className="flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-60">
                          <Download className="mr-2 h-4 w-4" />
                          {isDownloadingReport ? t('loading') : t('assess_download')}
                        </button>
                        <button onClick={printReport} className="flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                          <FileText className="mr-2 h-4 w-4" />
                          {t('assess_print')}
                        </button>
                        <button
                          onClick={() => {
                            setCurrentStep(1);
                            setSelectedPet(null);
                            setUploadedImages([]);
                            setImageAnalysis([]);
                            setChatMessages([]);
                            setAssessmentData(null);
                            setConsultImageIds([]);
                            setConsultMessage('');
                          }}
                          className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
                        >
                          {t('assess_new')}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="rounded-lg bg-white shadow">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">{t('assess_progress')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <Heart className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{t('assess_pet_selected')}</p>
                      <p className="text-xs text-gray-500">{selectedPet ? selectedPet.name : t('assess_not_selected')}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <Camera className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{t('assess_images_uploaded')}</p>
                      <p className="text-xs text-gray-500">{t('assess_photos_count', { count: uploadedImages.length })}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <MessageCircle className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{t('assess_chat_messages')}</p>
                      <p className="text-xs text-gray-500">{t('assess_messages_count', { count: chatMessages.length })}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <FileText className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{t('assess_report_status')}</p>
                      <p className="text-xs text-gray-500">{assessmentData ? t('assess_generated') : t('assess_pending')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assessment;
